import { mirrorWidth, MARGINS, type Site, type VehicleDims } from "@/lib/vehicle-fit";

/**
 * Måttriktig planvy ovanifrån. Ren SVG så den renderas i SSR och skalar
 * skarpt på mobilen.
 */
export function FitPlan({ dims, site }: { dims: VehicleDims; site: Site }) {
  const w = mirrorWidth(dims);
  const padding = 1.2;
  const contentW = Math.max(site.driveWidth, w);
  // markisen fälls ut mot höger – ge den plats i vyn så etiketten inte klipps
  const awningPad = dims.awning + 0.4;
  const worldW = contentW + padding * 2 + awningPad;
  const worldL = Math.max(site.driveLength, dims.length) + padding * 2;
  const scale = 60; // px per meter i viewBox-koordinater
  const vbW = worldW * scale;
  const vbL = worldL * scale;

  const driveX = (padding + (contentW - site.driveWidth) / 2) * scale;
  const driveY = padding * scale;
  const vehW = w * scale;
  const vehL = dims.length * scale;
  const vehX = (padding + (contentW - w) / 2) * scale;
  const vehY = driveY + MARGINS.rearAccess * scale * 0.5;
  const bodyInset = ((w - dims.width) / 2) * scale;

  const label = (
    x: number,
    y: number,
    text: string,
    anchor: "start" | "middle" | "end" = "middle",
  ) => (
    <text
      x={x}
      y={y}
      textAnchor={anchor}
      className="fill-current"
      style={{ fontSize: 15, fontVariantNumeric: "tabular-nums" }}
    >
      {text}
    </text>
  );

  const fmt = (v: number) => `${v.toFixed(2).replace(".", ",")} m`;

  return (
    <div className="not-prose overflow-hidden rounded-3xl border border-border bg-card">
      <svg
        viewBox={`0 0 ${vbW} ${vbL}`}
        className="h-auto w-full text-muted-foreground"
        role="img"
        aria-label={`Planvy: fordon ${fmt(dims.length)} x ${fmt(w)} på en uppfart ${fmt(site.driveLength)} x ${fmt(site.driveWidth)}`}
      >
        <defs>
          <pattern id="fitgrid" width={scale} height={scale} patternUnits="userSpaceOnUse">
            <path
              d={`M ${scale} 0 L 0 0 0 ${scale}`}
              fill="none"
              stroke="currentColor"
              strokeOpacity="0.15"
              strokeWidth="1"
            />
          </pattern>
        </defs>
        <rect width={vbW} height={vbL} fill="url(#fitgrid)" />

        {/* Uppfart */}
        <rect
          x={driveX}
          y={driveY}
          width={site.driveWidth * scale}
          height={site.driveLength * scale}
          rx="6"
          className="fill-accent/5 stroke-accent/50"
          strokeWidth="2"
          strokeDasharray="8 6"
        />

        {/* Markis (utfälld mot höger) */}
        <rect
          x={vehX + vehW}
          y={vehY + vehL * 0.15}
          width={dims.awning * scale}
          height={vehL * 0.6}
          className="fill-accent/10 stroke-accent/30"
          strokeWidth="1.5"
          strokeDasharray="5 5"
        />
        {label(vehX + vehW + (dims.awning * scale) / 2, vehY + vehL * 0.15 + 22, "markis")}

        {/* Fordon: kaross + speglar */}
        <rect
          x={vehX + bodyInset}
          y={vehY}
          width={dims.width * scale}
          height={vehL}
          rx="10"
          className="fill-foreground/85"
        />
        <rect
          x={vehX}
          y={vehY + vehL * 0.08}
          width={vehW}
          height={16}
          rx="6"
          className="fill-foreground/50"
        />
        {/* Dörrsvep */}
        <path
          d={`M ${vehX + bodyInset + dims.width * scale} ${vehY + vehL * 0.55}
              a ${MARGINS.doorSwing * scale} ${MARGINS.doorSwing * scale} 0 0 1 ${MARGINS.doorSwing * scale} ${MARGINS.doorSwing * scale}`}
          fill="none"
          className="stroke-accent"
          strokeWidth="2"
        />

        {/* Måttlinjer */}
        <line
          x1={driveX - 14}
          y1={driveY}
          x2={driveX - 14}
          y2={driveY + site.driveLength * scale}
          stroke="currentColor"
          strokeWidth="1.5"
        />
        {label(driveX - 22, driveY + (site.driveLength * scale) / 2, fmt(site.driveLength), "end")}

        <line
          x1={driveX}
          y1={driveY + site.driveLength * scale + 16}
          x2={driveX + site.driveWidth * scale}
          y2={driveY + site.driveLength * scale + 16}
          stroke="currentColor"
          strokeWidth="1.5"
        />
        {label(
          driveX + (site.driveWidth * scale) / 2,
          driveY + site.driveLength * scale + 38,
          `${fmt(site.driveWidth)} uppfart`,
        )}
        {label(vehX + vehW / 2, vehY + vehL / 2, `${fmt(dims.length)} × ${fmt(w)}`)}
      </svg>
    </div>
  );
}
